import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getPermanentTransferRequestsQueryOptions,
  permanentTransferRequestsQueryKey,
  type PermanentTransferRequestRow,
} from '@/data/transfers';
import type { TransferRecord, TransferRecordEvent } from '@/data/transferRecord';
import { formatDayRange, formatDateTime, pluralize, sentenceCaseStatus } from '@/domain/warehouse';
import { decidePermanentTransfer } from '@/api/transfers';
import { StatusChip } from '../../../components/status/StatusChip';
import useSessionStore from '../../../state/session';
import { useRoles } from '../../../hooks/useRoles';
import { useToasts } from '../../../ui/ToastProvider';
import TransferActionButton from './TransferActionButton';
import { eventLabel, formatSiteDay, siteName, type TransferActionViews } from './transferRecordView';

type TransferEventsSectionProps = {
  record: TransferRecord;
  actions: TransferActionViews;
  busy: boolean;
  onLinkEvents: () => void;
  onUnlinkEvent: (eventId: string) => void;
  /** The record changed outside the runner (a permanent-transfer decision). */
  onRecordChanged: () => void;
};

/** How an event is tied to the transfer: a link row (Unlink removes it), its lines, or both. */
function eventSource(event: TransferRecordEvent): string {
  if (event.linked && event.onLines) return 'Linked, and a line here is for it';
  if (event.linked) return 'Linked';
  return 'A line here is for it';
}

/** Events: the events this transfer supplies, the request it answers, and keep requests to decide. */
export default function TransferEventsSection({
  record,
  actions,
  busy,
  onLinkEvents,
  onUnlinkEvent,
  onRecordChanged,
}: TransferEventsSectionProps) {
  const { transfer, events, totals } = record;
  const request = transfer.request;
  const toZone = transfer.toSite?.timezone ?? null;
  const fromZone = transfer.fromSite?.timezone ?? null;
  const requestedOn = request ? formatSiteDay(request.requestedAt, toZone) : null;
  const acceptedOn = request ? formatSiteDay(request.acceptedAt, fromZone) : null;

  return (
    <div className="transfer-record-panel">
      <div className="transfer-record-panel__head">
        <div className="transfer-record-panel__heading">
          <h2 className="transfer-record-panel__title">Linked events</h2>
          <p className="transfer-record-panel__intro">
            Events this transfer supplies, and the request it answers. An event a line here is for stays listed while that line does.
          </p>
        </div>
        <div className="transfer-record-panel__actions">
          <TransferActionButton className="btn sm" allowed={actions.link_event.allowed} reason={actions.link_event.reason} busy={busy} onClick={onLinkEvents}>
            Link an event
          </TransferActionButton>
        </div>
      </div>

      {events.length ? (
        <ul className="transfer-record-links" aria-label="Linked events">
          {events.map((event) => {
            const dates = formatDayRange(event.startDate, event.endDate === event.startDate ? null : event.endDate);
            const units = event.unitsOnTransfer != null && event.unitsOnTransfer > 0
              ? `${event.unitsOnTransfer.toLocaleString()} of ${pluralize(totals.units, 'unit')} on this transfer`
              : null;
            const meta = [eventSource(event), dates, event.site?.name, event.status ? sentenceCaseStatus(event.status) : null, units]
              .filter(Boolean).join(' · ');
            return (
              <li key={event.id} className="transfer-record-link-row">
                <div className="transfer-record-link-row__main">
                  <Link className="transfer-record-link-row__title" to={`/events/${encodeURIComponent(event.id)}`}>{eventLabel(event)}</Link>
                  {meta ? <span>{meta}</span> : null}
                </div>
                {/* Unlink removes a link row; an event only its lines carry has none to remove. */}
                {event.linked ? (
                  <TransferActionButton
                    className="btn xs"
                    allowed={actions.link_event.allowed}
                    reason={actions.link_event.reason}
                    busy={busy}
                    ariaLabel={`Unlink ${eventLabel(event)}`}
                    onClick={() => onUnlinkEvent(event.id)}
                  >
                    Unlink
                  </TransferActionButton>
                ) : null}
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="transfer-record-empty">Not linked to an event yet.</p>
      )}

      <h3 className="transfer-record-panel__subhead">Request this transfer answers</h3>
      {request ? (
        <div className="transfer-record-link-row">
          <div className="transfer-record-link-row__main">
            <strong>
              {[request.itemName, request.qty != null ? pluralize(request.qty, 'unit') : null].filter(Boolean).join(', ') || 'Transfer request'}
            </strong>
            <span>
              {[
                request.requestedBySite
                  ? `Requested by ${request.requestedBySite.name ?? 'a site'}${requestedOn ? ` on ${requestedOn}` : ''}`
                  : null,
                request.acceptedBySite
                  ? `accepted by ${request.acceptedBySite.name ?? 'a site'}${acceptedOn ? ` on ${acceptedOn}` : ''}`
                  : null,
              ].filter(Boolean).join(' · ')}
            </span>
          </div>
          <StatusChip kind="transferRequest" status={request.status} size="sm" />
        </div>
      ) : (
        <p className="transfer-record-empty">This transfer was not made from a request.</p>
      )}

      <PermanentTransferRequests record={record} onDecided={onRecordChanged} />
    </div>
  );
}

const DECIDED_MESSAGE = {
  approved: 'Ownership transferred to destination',
  rejected: 'Keep request rejected',
} as const;

/**
 * Keep requests (permanent transfers) waiting on the shipping site: anyone at that site, or an
 * admin, approves or rejects them; the server checks it (docs/permanent-transfer-decisions.md).
 * The heading and card markup are what e2e/permanent-transfer-decide.journey.mjs reads.
 */
function PermanentTransferRequests({ record, onDecided }: { record: TransferRecord; onDecided: () => void }) {
  const { transfer } = record;
  const queryClient = useQueryClient();
  const { push } = useToasts();
  const { isAdmin } = useRoles();
  const homeLocationId = useSessionStore((state) => state.profile?.home_location_id ?? null);
  const allowedLocationIds = useSessionStore((state) => state.allowedLocationIds);
  const [deciding, setDeciding] = React.useState<string | null>(null);
  const requestsQuery = useQuery(getPermanentTransferRequestsQueryOptions({ transferId: transfer.id, enabled: Boolean(transfer.id) }));
  const fromSiteId = transfer.fromSite?.id ?? null;
  const atSourceSite = Boolean(fromSiteId) && (fromSiteId === homeLocationId || allowedLocationIds.includes(fromSiteId as string));
  const pending = React.useMemo(
    () => (requestsQuery.data ?? []).filter((row: PermanentTransferRequestRow) => row.status === 'pending'),
    [requestsQuery.data],
  );

  const decide = React.useCallback(async (requestId: string, decision: 'approved' | 'rejected') => {
    setDeciding(requestId);
    try {
      await decidePermanentTransfer({ requestId, decision });
      push({ type: 'success', message: DECIDED_MESSAGE[decision] });
    } catch (error) {
      const message = error && typeof error === 'object' && 'message' in error ? String((error as { message?: unknown }).message ?? '') : '';
      push({ type: 'error', message: message || 'The request was not decided. Try again.' });
    } finally {
      setDeciding(null);
      await queryClient.invalidateQueries({ queryKey: permanentTransferRequestsQueryKey(transfer.id) });
      onDecided();
    }
  }, [onDecided, push, queryClient, transfer.id]);

  if (!(atSourceSite || isAdmin) || !pending.length) return null;

  return (
    <section className="transfer-record-permanent" aria-label="Pending permanent transfer requests">
      <h3 className="transfer-record-panel__subhead">Pending permanent transfer requests</h3>
      <p className="transfer-record-panel__intro">
        {siteName(transfer.toSite, 'The receiving site')} asks to keep these units. Approving moves their ownership there.
      </p>
      <div className="stack transfer-record-permanent__list">
        {pending.map((row) => (
          <div key={row.request_id} className="transfer-record-permanent__card">
            <div className="fw-semibold transfer-record-permanent__item">{row.item?.name || 'Item'}</div>
            {row.asset?.serial_number ? <div className="text-xs fg-muted">Asset: {row.asset.serial_number}</div> : null}
            <div className="transfer-record-permanent__facts">
              <div>
                <div className="text-xs">Requested Qty</div>
                <div>{row.requested_qty}</div>
              </div>
              <div>
                <div className="text-xs">Requested By</div>
                <div>{row.requested_by_profile?.full_name || 'Unknown'}</div>
              </div>
              <div>
                <div className="text-xs">Requested On</div>
                <div>{formatDateTime(row.requested_at) ?? '—'}</div>
              </div>
            </div>
            {row.reason ? <p className="transfer-record-permanent__reason">{row.reason}</p> : null}
            <div className="transfer-record-permanent__actions">
              <button
                type="button"
                className="btn sm btn-primary"
                onClick={() => { void decide(row.request_id, 'approved'); }}
                disabled={deciding === row.request_id}
              >
                {deciding === row.request_id ? 'Processing…' : 'Approve'}
              </button>
              <button
                type="button"
                className="btn sm danger"
                onClick={() => { void decide(row.request_id, 'rejected'); }}
                disabled={deciding === row.request_id}
              >
                {deciding === row.request_id ? 'Processing…' : 'Reject'}
              </button>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
